<template>
  <b-card class="mb-3">
    <b-card-body>
      <!-- Filtro de Pessoa Jurídica (apenas para admin) -->
      <b-row v-if="isAdmin">
        <b-col cols="12">
          <select-suggestion
            :key="selectLegalEntityKey"
            ref="selectLegalEntity"
            label="Pessoa Jurídica"
            column="company_name,trading_name,cnpj"
            model="LegalEntity"
            :selectedModel="selectedLegalEntity"
            @selected-model="$emit('update:selectedLegalEntity', $event)"
            :appendClearOption="true"
            placeholderText="Digite o nome ou cnpj da pessoa jurídica"
            :itemTextFormat="legalEntityItemTextFormat"
          />
        </b-col>
      </b-row>

      <!-- Slot para filtros adicionais (ex: protocol_number) -->
      <slot name="additional-filters"></slot>

      <!-- Filtros de Data -->
      <b-row :class="hasAdditionalFilters ? 'mt-2' : 'mt-2'">
        <b-col cols="12" :md="hasAdditionalFilters ? 3 : 4">
          <b-form-group :label="$t(`${reportTypePrefix}.filters.start_date`)">
            <datepicker
              :value="startDate"
              @input="$emit('update:startDate', $event)"
              format="dd/MM/yyyy"
              :placeholder="
                $t(`${reportTypePrefix}.filters.start_date_placeholder`)
              "
              input-class="form-control"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" :md="hasAdditionalFilters ? 3 : 4">
          <b-form-group :label="$t(`${reportTypePrefix}.filters.end_date`)">
            <datepicker
              :value="endDate"
              @input="$emit('update:endDate', $event)"
              format="dd/MM/yyyy"
              :placeholder="
                $t(`${reportTypePrefix}.filters.end_date_placeholder`)
              "
              input-class="form-control"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Botões de Ação -->
      <div class="w-full flex gap-2 justify-end mt-3">
        <b-button
          variant="primary"
          @click="$emit('search')"
          size="sm"
          :disabled="loading"
        >
          {{ $t('action.search') }}
          <feather-icon
            v-if="loading"
            icon="LoaderIcon"
            size="14"
            class="animate-spin"
          />
          <feather-icon v-else icon="SearchIcon" size="14" />
        </b-button>
        <b-button
          variant="secondary"
          @click="handleClear"
          size="sm"
          class="mr-1"
          :disabled="loading"
        >
          {{ $t('action.clear') }}
          <feather-icon icon="RefreshCwIcon" size="14" />
        </b-button>
        <b-button
          v-if="showExportButton && hasData"
          variant="success"
          @click="$emit('export')"
          size="sm"
          :disabled="loading"
        >
          <feather-icon icon="FileTextIcon" size="14" class="mr-1" />
          {{ $t(`${reportTypePrefix}.export_pdf`) }}
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormGroup
} from 'bootstrap-vue'
import Datepicker from 'educatena-vuejs-datepicker'
import SelectSuggestion from '@/components/SelectSuggestion.vue'
import { isAdmin } from '@/util/Util'

export default {
  name: 'ReportFilters',
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    Datepicker,
    SelectSuggestion
  },
  props: {
    selectedLegalEntity: {
      type: Object,
      default: null
    },
    startDate: {
      type: [Date, String],
      default: null
    },
    endDate: {
      type: [Date, String],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    reportType: {
      type: String,
      required: true,
      validator: value => {
        return [
          'response_time',
          'type_reporting',
          'identification',
          'severity'
        ].includes(value)
      }
    },
    showExportButton: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    isAdmin() {
      return isAdmin()
    },
    reportTypePrefix() {
      return `reporting_channel.reports.${this.reportType}`
    },
    hasAdditionalFilters() {
      return !!this.$slots['additional-filters']
    }
  },
  watch: {
    selectedLegalEntity(newVal) {
      // Quando selectedLegalEntity é definido como null externamente,
      // forçar re-renderização do SelectSuggestion usando key
      if (newVal === null) {
        this.selectLegalEntityKey += 1
      }
    }
  },
  methods: {
    legalEntityItemTextFormat(model) {
      let name = `${model.company_name} - ${model.trading_name}`
      const hasCnpj = !!model.cnpj
      if (hasCnpj) {
        name = `${name} ( ${this.cnpjFormat(model.cnpj)}) `
      }
      return name
    },
    cnpjFormat(cnpj) {
      if (!cnpj) return ''
      const cleaned = cnpj.replace(/\D/g, '')
      if (cleaned.length !== 14) return cnpj
      return cleaned.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
      )
    },
    handleClear() {
      // Limpar pessoa jurídica antes de emitir o evento
      this.$emit('update:selectedLegalEntity', null)
      // Forçar re-renderização do SelectSuggestion usando key
      this.selectLegalEntityKey += 1
      this.$emit('clear')
    }
  }
}
</script>

<style scoped></style>
